import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import { SIGNBOARDS, REGIONS } from "@/data/signboards";

/**
 * SignboardsMap — Wikimedia public-domain Japan SVG as backdrop, with
 * brand pins positioned **directly on the geographic locations**.
 *
 * Implementation: the map and pins live inside an aspect-locked wrapper
 * (`aspect-[570/755]`) sized to fit the container. Pins are HTML buttons
 * positioned absolutely in percentage of that wrapper, so they align
 * pixel-perfect with features in the underlying SVG.
 *
 * Pins are big & hoverable. Hover label is large + readable. Clicking
 * the Okinawa cluster expands a generous menu; clicking anywhere outside
 * the cluster button or menu closes it.
 */

const MAP_IMG_SRC = "/maps/japan-prefectures.svg";

// Pin positions in the Wikimedia SVG's native coordinate space
// (viewBox is 0 0 570 755). We convert to percentages of the
// aspect-locked wrapper, which is geometrically identical.
const YOKOSUKA_PIN_PCT = { x: (433 / 570) * 100, y: (560 / 755) * 100 };
const OKINAWA_PIN_PCT  = { x: (503 / 570) * 100, y: (705 / 755) * 100 };

export default function SignboardsMap({ lang, isJa, variant = "default" }) {
  const navigate = useNavigate();
  const [okiOpen, setOkiOpen] = useState(false);
  const clusterButtonRef = useRef(null);
  const clusterMenuRef = useRef(null);

  // Close the cluster menu on any click that isn't on the cluster pin
  // or its expanded menu, including clicks on the map background itself.
  useEffect(() => {
    if (!okiOpen) return;
    const onDocClick = (e) => {
      const onButton = clusterButtonRef.current && clusterButtonRef.current.contains(e.target);
      const onMenu = clusterMenuRef.current && clusterMenuRef.current.contains(e.target);
      if (!onButton && !onMenu) setOkiOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOkiOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [okiOpen]);

  const heightClass =
    variant === "hero"
      ? "h-[380px] md:h-[440px]"
      : variant === "fullpage"
      ? "h-[60vh] md:h-[72vh]"
      : "h-[440px] md:h-[540px]";

  const yokosuka = SIGNBOARDS.find((b) => b.slug === "yokosuka-base");
  const okinawaBoards = SIGNBOARDS.filter((b) => b.region === "okinawa");
  const yokoRegion = REGIONS.find((r) => r.key === "yokosuka");
  const okiRegion = REGIONS.find((r) => r.key === "okinawa");

  return (
    <div
      data-testid={`signboards-map-${variant}`}
      className="rounded-3xl overflow-hidden ring-1 ring-[#E2E8F0] bg-[#5B2E83] shadow-[0_24px_60px_-24px_rgba(15,29,45,0.35)]"
    >
      {/* Top label band */}
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 bg-[#5B2E83] text-white">
        <span className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-bold">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#7AB8E8] opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7AB8E8]" />
          </span>
          {isJa ? "看板所在地マップ" : "Signboard locations · Japan"}
        </span>
        <span className="hidden md:inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.22em] uppercase font-bold text-white/70">
          {isJa ? "ピンをクリックで詳細へ" : "Click a pin for details"}
        </span>
      </div>

      {/* Map canvas — centered aspect-locked wrapper aligns pin coordinates
          with the underlying SVG. Outer container flex-centers so the
          wrapper is centered within available space. */}
      <div className={`relative w-full ${heightClass} bg-[#F5F8FB] flex items-center justify-center overflow-visible`}>
        <div
          className="relative h-full"
          style={{ aspectRatio: "570 / 755", maxWidth: "100%" }}
        >
          <img
            src={MAP_IMG_SRC}
            alt={isJa ? "日本地図 — 都道府県" : "Map of Japan — prefectures"}
            className="absolute inset-0 w-full h-full select-none pointer-events-none"
            draggable={false}
          />

          {/* Yokosuka pin (single) */}
          <SinglePin
            x={YOKOSUKA_PIN_PCT.x}
            y={YOKOSUKA_PIN_PCT.y}
            accent={yokoRegion?.accent || "#0E5FB0"}
            label={yokosuka?.title[lang] || ""}
            ariaLabel={isJa ? `${yokosuka?.title.ja} の詳細ページへ` : `Go to ${yokosuka?.title.en} detail page`}
            testId={`map-pin-${yokosuka?.slug}`}
            onClick={() => navigate(`/${lang}/signboards/${yokosuka?.slug}`)}
          />

          {/* Okinawa cluster pin (3 boards) — the EXPANDED MENU is rendered
              separately below so it can sit at the centre of the map regardless
              of where the pin lives geographically. */}
          <div
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: `${OKINAWA_PIN_PCT.x}%`, top: `${OKINAWA_PIN_PCT.y}%` }}
          >
            <button
              ref={clusterButtonRef}
              type="button"
              onClick={() => setOkiOpen((o) => !o)}
              data-testid="map-cluster-okinawa"
              aria-expanded={okiOpen}
              aria-controls="signboards-map-okinawa-cluster-menu"
              aria-label={isJa ? `沖縄の看板 ${okinawaBoards.length} 件を表示` : `Show ${okinawaBoards.length} Okinawa signboards`}
              className="group relative focus:outline-none"
            >
              <span className="block transition-transform group-hover:-translate-y-1 group-focus-visible:-translate-y-1">
                <svg aria-hidden="true" width="44" height="56" viewBox="0 0 32 42"
                     className="drop-shadow-[0_4px_6px_rgba(15,29,45,0.55)]">
                  <path
                    d="M16 1.5C8.5 1.5 2.5 7.5 2.5 15c0 9.6 11 22.5 13.5 25.5 2.5-3 13.5-15.9 13.5-25.5 0-7.5-6-13.5-13.5-13.5z"
                    fill={okiRegion?.accent || "#2F8F3C"}
                    stroke="#ffffff"
                    strokeWidth="2"
                  />
                  <circle cx="16" cy="15" r="8" fill="#ffffff" />
                  <text x="16" y="18.4" textAnchor="middle"
                        fontSize="11" fontWeight="800"
                        fill={okiRegion?.accent || "#2F8F3C"}>
                    {okinawaBoards.length}
                  </text>
                </svg>
              </span>
              {!okiOpen && (
                <span
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 whitespace-nowrap rounded-lg bg-[#5B2E83] text-white text-[13px] font-semibold tracking-tight px-3 py-1.5 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity shadow-[0_12px_28px_-10px_rgba(15,29,45,0.6)]"
                >
                  {isJa ? `沖縄 · 看板 ${okinawaBoards.length} 件` : `Okinawa · ${okinawaBoards.length} signboards`}
                </span>
              )}
            </button>
          </div>

          {/* Centred cluster menu — rendered as a centered overlay so it's
              fully visible regardless of where the cluster pin sits. */}
          {okiOpen && (
            <div
              ref={clusterMenuRef}
              id="signboards-map-okinawa-cluster-menu"
              data-testid="map-cluster-okinawa-menu"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(320px,86%)] rounded-2xl bg-white ring-1 ring-[#E2E8F0] shadow-[0_30px_70px_-20px_rgba(15,29,45,0.55)] overflow-hidden z-20 animate-brand-fade-in"
            >
              <div className="h-1.5 w-full" style={{ backgroundColor: okiRegion?.accent || "#2F8F3C" }} />
              <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-[#F1F5F9]">
                <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#0F1D2D]">
                  {isJa ? "沖縄" : "Okinawa"}
                </span>
                <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-[#94A3B8]">
                  {okinawaBoards.length} {isJa ? "件" : "boards"}
                </span>
              </div>
              <ul className="py-1.5">
                {okinawaBoards.map((b) => (
                  <li key={b.slug}>
                    <button
                      type="button"
                      onClick={() => { setOkiOpen(false); navigate(`/${lang}/signboards/${b.slug}`); }}
                      data-testid={`map-cluster-item-${b.slug}`}
                      className="w-full text-left px-4 py-2.5 hover:bg-[#F1F5F9] focus:outline-none focus-visible:bg-[#F1F5F9] transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: okiRegion?.accent || "#2F8F3C" }} />
                        <span className="text-[14px] font-semibold tracking-tight text-[#0F1D2D] leading-tight truncate">
                          {b.title[lang]}
                        </span>
                      </div>
                      {b.route && (
                        <div className="ml-5 mt-1 text-[11px] tracking-[0.14em] uppercase font-bold text-[#94A3B8]">
                          {b.route}
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Footer legend — chips route to per-region detail map pages */}
      <div className="bg-white border-t border-[#E2E8F0] px-4 md:px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        {REGIONS.filter((r) => SIGNBOARDS.some((b) => b.region === r.key)).map((r) => {
          const count = SIGNBOARDS.filter((b) => b.region === r.key).length;
          const path = r.key === "okinawa" ? "okinawa" : "mainland";
          return (
            <Link
              key={r.key}
              to={`/${lang}/signboards/map/${path}`}
              data-testid={`map-legend-chip-${r.key}`}
              className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[12px] font-semibold tracking-tight text-[#0F1D2D] transition-colors group"
            >
              <span className="h-2.5 w-2.5 rounded-full ring-2 ring-white shadow" style={{ backgroundColor: r.accent }} />
              {isJa ? r.ja : r.en}
              <span className="text-[#94A3B8] font-mono text-[11px] tabular-nums">· {count}</span>
              <span className="text-[#94A3B8] group-hover:text-[#0F1D2D] transition-colors" aria-hidden="true">→</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function SinglePin({ x, y, accent, label, ariaLabel, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={ariaLabel}
      className="group absolute -translate-x-1/2 -translate-y-full focus:outline-none"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      <span className="block transition-transform group-hover:-translate-y-1 group-focus-visible:-translate-y-1">
        <svg aria-hidden="true" width="40" height="52" viewBox="0 0 32 42"
             className="drop-shadow-[0_4px_6px_rgba(15,29,45,0.55)]">
          <path
            d="M16 1.5C8.5 1.5 2.5 7.5 2.5 15c0 9.6 11 22.5 13.5 25.5 2.5-3 13.5-15.9 13.5-25.5 0-7.5-6-13.5-13.5-13.5z"
            fill={accent}
            stroke="#ffffff"
            strokeWidth="2"
          />
          <circle cx="16" cy="15" r="6" fill="#ffffff" />
        </svg>
      </span>
      <span
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 whitespace-nowrap rounded-lg bg-[#5B2E83] text-white text-[13px] font-semibold tracking-tight px-3 py-1.5 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity shadow-[0_12px_28px_-10px_rgba(15,29,45,0.6)]"
      >
        {label}
      </span>
    </button>
  );
}
